import { useFetcher } from "@remix-run/react";
import React, { useEffect, useId, useRef } from "react";
import { Button } from "~/components/form/Button";
import { Field } from "~/components/form/Field";
import { TextField } from "~/components/form/TextField";
import { TextareaField } from "~/components/form/TextareaField";
import { ChevronLeftIcon } from "~/components/icons/ChevronLeftIcon";
import { CrossIcon } from "~/components/icons/CrossIcon";
import { EmailIcon } from "~/components/icons/EmailIcon";
import { PhoneIcon } from "~/components/icons/PhoneIcon";
import { ReportIssueIcon } from "~/components/icons/ReportIssueIcon";
import { Modal } from "~/components/overlays/Modal";
import { Heading } from "~/components/typography/Heading";
import { ROUTES } from "~/consts";

interface SupportOptionsProps {
	handleShowReportProblemForm?: () => void;
	showReportProblemLink?: boolean;
}

export const SupportOptions = ({
	handleShowReportProblemForm,
	showReportProblemLink = true,
}: SupportOptionsProps) => (
	<div className="@container">
		<div className="grid grid-cols-1 @3xl:grid-cols-3 gap-2">
			<a
				href="mailto:support@tarifftribe.co.uk"
				className="p-4 rounded-lg bg-white border border-gray-300 flex items-center gap-4 justify-between"
			>
				<div>
					<p className="font-medium">
						Email us{" "}
						<span className="text-sm text-secondary-500 font-normal">
							(support@tarifftribe.co.uk)
						</span>
					</p>
					<p className="text-sm text-secondary-500">
						We aim to answer you within 1 business day
					</p>
				</div>
				<div>
					<EmailIcon />
				</div>
			</a>

			<a
				href="tel:07943618328"
				className="p-4 rounded-lg bg-white border border-gray-300 flex items-center gap-4 justify-between"
			>
				<div>
					<p className="font-medium">
						Leave us a voicemail{" "}
						<span className="text-sm text-secondary-500 font-normal">
							(07943 618 328)
						</span>
					</p>
					<p className="text-sm text-secondary-500">
						Available Monday to Friday, 9AM - 5PM
					</p>
				</div>
				<div>
					<PhoneIcon />
				</div>
			</a>

			{showReportProblemLink && (
				<button
					type="button"
					name="report_problem"
					onClick={handleShowReportProblemForm}
					className="p-4 rounded-lg bg-white border border-gray-300 flex items-center gap-4 justify-between text-left"
				>
					<div>
						<p className="font-medium">Report a problem</p>
						<p className="text-sm text-secondary-500">
							We aim to answer you within 1 business day
						</p>
					</div>
					<div>
						<ReportIssueIcon />
					</div>
				</button>
			)}
		</div>
	</div>
);

interface FormResponse {
	error: {
		subject?: FormFieldError;
		message?: FormFieldError;
	};
	success?: boolean;
}

interface ReportProblemFormProps {
	subject?: string;
	message?: string;
}

export const ReportProblemForm = ({
	subject,
	message,
}: ReportProblemFormProps) => {
	const fetcher = useFetcher();
	const formRef = useRef<HTMLFormElement>(null);
	const actionData = fetcher.data as FormResponse;
	const isSubmitting = fetcher.state !== "idle";

	// Reset form after successful submission
	useEffect(() => {
		if (fetcher.state === "idle" && actionData?.success) {
			formRef.current?.reset();
		}
	}, [fetcher.state, actionData]);

	return (
		<div className="contents">
			{actionData?.success === true ? (
				<div className="bg-gray-300 p-5 rounded-md">
					<p>Your message has been sent to our team.</p>
				</div>
			) : actionData?.success === false ? (
				<div className="bg-primary-300 p-5 rounded-md">
					<p>
						Sorry, we were unable to send your report. Please try again later,
						or send a direct email to{" "}
						<a className="underline" href="mailto:support@tarifftribe.co.uk">
							support@tarifftribe.co.uk
						</a>
					</p>
				</div>
			) : null}
			<fetcher.Form
				ref={formRef}
				className="flex flex-col gap-2"
				action={ROUTES.company.support.reportProblem}
				method="post"
			>
				<Field errors={actionData?.error?.subject?._errors}>
					<TextField
						name="subject"
						label="Subject"
						defaultValue={subject}
						required={true}
						invalid={!!actionData?.error?.subject}
					/>
				</Field>
				<Field errors={actionData?.error?.message?._errors}>
					<TextareaField
						name="message"
						label="Your message"
						placeholder="Enter your message"
						defaultValue={message}
						rows={6}
						required={true}
						invalid={!!actionData?.error?.message}
					/>
				</Field>
				<Button
					name="submit"
					label={isSubmitting ? "Submitting..." : "Submit"}
					type="submit"
					styleType="primary-alt"
					disabled={isSubmitting}
				/>
			</fetcher.Form>
		</div>
	);
};

interface CompanySupportModalProps {
	hidden?: boolean;
	handleDismiss: () => void;
}

export const CompanyOnboardSupportModal = ({
	hidden,
	handleDismiss,
}: CompanySupportModalProps) => {
	const [showReportProblemForm, setShowReportProblemForm] =
		React.useState(false);

	const closeAndResetModal = () => {
		handleDismiss();
		setShowReportProblemForm(false);
	};
	return (
		<Modal hidden={hidden} handleDismiss={closeAndResetModal}>
			<div className="flex flex-col gap-6 pt-2 pb-3 px-3">
				<div className="flex justify-between items-center">
					<div className="flex items-center gap-1">
						{showReportProblemForm && (
							<button
								type="button"
								onClick={() => setShowReportProblemForm(false)}
							>
								<ChevronLeftIcon />
							</button>
						)}
						<Heading tagName="h5" styleType="semibold">
							{showReportProblemForm ? "Report a problem" : "Support"}
						</Heading>
					</div>
					<div
						className="cursor-pointer"
						onClick={closeAndResetModal}
						onKeyUp={closeAndResetModal}
					>
						<CrossIcon />
					</div>
				</div>

				{showReportProblemForm ? (
					<ReportProblemForm />
				) : (
					<div className="contents">
						<p className="text-secondary-500">
							For any assistance, you can call us, email us, or report a problem
							using the options below
						</p>
						<SupportOptions
							handleShowReportProblemForm={() => setShowReportProblemForm(true)}
						/>
					</div>
				)}
			</div>
		</Modal>
	);
};
